import { useId, type ReactNode } from "react";

type Props = {
  label: string;
  hint?: string;
  error?: string | undefined;
  className?: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
};

/**
 * A label, its control, an optional hint and its error, wired together for screen readers.
 * The hint and the error share the label's own row, the error taking the hint's place: the contact page has to fit one window, and an error
 * that added a line under each field would push the send button out of it.
 */
export function FormField({ label, hint, error, className, children }: Props) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div
      className={["form-field", className].filter(Boolean).join(" ")}
      data-invalid={Boolean(error)}
    >
      <div className="form-field__top">
        <label className="form-field__label" htmlFor={id}>
          {label}
        </label>
        {hint ? (
          <p className="form-field__hint" id={hintId}>
            {hint}
          </p>
        ) : null}
        {error ? (
          <p className="form-field__error" id={errorId}>
            {error}
          </p>
        ) : null}
      </div>
      {children({ id, describedBy, invalid: Boolean(error) })}
    </div>
  );
}
